export const SocketConnectionStatuses = {
  Disconnected: 'disconnected',
  Connecting: 'connecting',
  Connected: 'connected',
  Failed: 'failed',
  Destroyed: 'destroyed',
};

let uniqueId = 0;

const baseLogger = (args, type) => {
  console[type](...args);
};

const defaults = {
  name: 'WebSocket',
  maxFailures: 200,
  maxReconnects: 13,
  silenceTimeoutSeconds: 20,
  connectingTimeoutSeconds: 20,
  onStatusChanged: () => {},
  onDeleteSubscriptionError: () => {},
  pollingTimeInterval: 10000,
  logger: baseLogger,
};

export class WSClient {
  id = uniqueId;

  keepAliveTimer;

  messageId = 0;

  url = null;

  status = SocketConnectionStatuses.Disconnected;

  handlerMap = {};

  throttleMap = {};

  subscriptionsList = [];

  failedMsgs = {};

  resetsCount = 0;

  silenceTimer = null;

  reconnectTimer = null;

  instance = null;

  onFail = null;

  onStatusChanged = null;

  onDeleteSubscriptionError = null;

  opts = null;

  logger = baseLogger;

  constructor(url, userOpts = {}) {
    uniqueId++;
    this.url = url;

    this.opts = { ...defaults, ...userOpts };

    const { onStatusChanged, onDeleteSubscriptionError, logger } = userOpts;
    if (onStatusChanged) {
      this.onStatusChanged = onStatusChanged;
    }
    if (onDeleteSubscriptionError) {
      this.onDeleteSubscriptionError = onDeleteSubscriptionError;
    }
    this.prefix = `${this.opts.name} #${this.id}`;

    if (logger) {
      this.logger = logger;
    }
  }

  isOpen = () => this.status === SocketConnectionStatuses.Connected;

  addHandler = (config) => {
    this.handlerMap[config.id] = config;
  };

  /**
   * resend all msgs in this.failedMsgs
   */
  resendFailed = () => {
    Object.values(this.failedMsgs).forEach(this.send);
  };

  clearFailedMsgs = () => {
    this.failedMsgs = {};
  };

  send = (config) => {
    const configWithId = {
      ...config,
      id: this.messageId,
    };
    this.messageId++;
    if (!this.isOpen()) {
      this.log(`push to msg queue, no connection ${this.messageId}`);
      const foundInSubs = this.subscriptionsList.findIndex(
        (el) => JSON.stringify(el) === JSON.stringify(config)
      );
      if (foundInSubs === -1) {
        this.failedMsgs[this.messageId] = configWithId;
      }
      return;
    }

    const { callback, throttleInterval, onSend, ...message } = configWithId;

    const throttle = this.throttleMap[config.type];
    if (!throttle) this.throttleMap[config.type] = {};
    // send only if its not blocked by self made throttle
    if (
      !config.throttleInterval ||
      (config.throttleInterval && throttle && !throttle.value)
    ) {
      try {
        this.addHandler(configWithId);
        this.instance.send(JSON.stringify(message));
        if (onSend) onSend(configWithId);
      } catch (error) {
        this.error('sending error: ', error);
        this.failedMsgs[this.messageId] = configWithId;
      }
    }

    if (config.throttleInterval && !throttle.value) {
      throttle.value = true;
      throttle.timeout = setTimeout(() => {
        throttle.value = false;
      }, config.throttleInterval);
    }

    return configWithId;
  };

  sendSubscribeMessage = (configObjOrFn, isSub) => {
    const configIsFunction = typeof configObjOrFn === 'function';
    let configObj = configObjOrFn;

    if (configIsFunction) {
      configObj = configObjOrFn();
    }

    if (typeof isSub === 'boolean' && isSub) {
      this.subscriptionsList.push(configObj);
    }

    if (!this.isOpen()) {
      return;
    }

    const { isRepeat, ...restConfig } = configObj;

    const restConfigFn = () => ({
      ...configObjOrFn(),
      isRepeat,
    });

    if (isRepeat !== false) {
      configObj.timeout = setTimeout(
        () =>
          this.sendSubscribeMessage(
            configIsFunction ? restConfigFn : restConfig
          ),
        configObj.pollingInterval || this.opts.pollingTimeInterval
      );
    }

    return this.send(configObj);
  };

  onOpen = () => {
    this.changeStatus(SocketConnectionStatuses.Connected);
    this.log(this);
    this.subscriptionsList.forEach(this.sendSubscribeMessage);
    this.resendFailed();
    if (this.cb) {
      this.cb();
    }
    this.resetsCount = 0;

    this.keepAliveTimer = setInterval(() => {
      this.instance.send('ping');
    }, 20000);
    clearTimeout(this.reconnectTimer);
  };

  onClose = () => {
    clearInterval(this.keepAliveTimer);
    if (this.status === SocketConnectionStatuses.Destroyed) {
      return;
    }
    if (this.resetsCount >= this.opts.maxFailures) {
      this.fail(
        `WebSocket failed to reconnect ${this.opts.maxFailures} times.`
      );
      this.clearFailedMsgs();
      return;
    }
    this.changeStatus(SocketConnectionStatuses.Disconnected);
    if (this.resetsCount >= this.opts.maxReconnects) {
      this.changeStatus(SocketConnectionStatuses.Failed);
      this.warn(
        `WebSocket tried to reconnect ${this.opts.maxReconnects} times, but no success.`
      );
      this.clearFailedMsgs();
      return;
    }
    this.reconnectTimer = setTimeout(() => {
      this.log(`reconnecting to ws after ${this.resetsCount} seconds...`);
      this.init(this.cb); // retry connection
      this.resetsCount++;
    }, 1000 * this.resetsCount);
  };

  onMessage = (event) => {
    if (event.type !== 'message') {
      this.error(`unexpected event type ${event}`);
      return;
    }
    try {
      const message = JSON.parse(event.data);
      const messageType = message.type;

      const { id } = message;
      const handler = this.handlerMap[id];

      if (handler && handler.callback && !handler.handleType) {
        handler.callback(message.body, message);
      } else {
        Object.values(this.handlerMap).forEach((subscriber) => {
          if (messageType === subscriber.handleType && subscriber.callback) {
            subscriber.callback(message.body, message);
          }
        });
      }

      clearTimeout(this.silenceTimer);
      this.silenceTimer = setTimeout(() => {
        this.fail(
          `WS was silent for almost ${this.opts.silenceTimeoutSeconds} secs, reconnecting.`
        );
      }, this.opts.silenceTimeoutSeconds * 1000);

      if (handler && !handler.nonDelete && !handler.isRepeat)
        delete this.handlerMap[id];
    } catch (error) {
      this.log('event data', event.data);
      this.error(error);
    }
  };

  init = (cb) => {
    this.cb = cb;
    this.changeStatus(SocketConnectionStatuses.Connecting);
    this.unsubscribe();
    this.reconnectTimer = setTimeout(() => {
      this.fail(
        `WS has not been connected for almost ${this.opts.connectingTimeoutSeconds} secs, reconnecting.`
      );
    }, this.opts.connectingTimeoutSeconds * 1000);
    this.instance = new WebSocket(this.url);

    this.instance.onopen = this.onOpen;
    this.instance.onclose = this.onClose;
    this.instance.onmessage = this.onMessage;
    this.instance.onerror = this.unsubscribe;

    return this.instance;
  };

  /**
   * Disconnect from socket message.
   */
  unsubscribe = () => {
    clearTimeout(this.silenceTimer);
    clearTimeout(this.reconnectTimer);
    if (this.instance) {
      this.log('closing WS connection with good reason');
      this.instance.close(1000); // close with "good" reason
    }
  };

  /* When things are bad - we need to do all stuff before going to error page */
  fail = (msg) => {
    this.error(msg);
    this.unsubscribe();
    if (this.onFail) this.onFail();
  };

  destroy = () => {
    this.changeStatus(SocketConnectionStatuses.Destroyed);
    this.unsubscribe();
    this.instance = null;
    this.clearFailedMsgs();
  };

  changeStatus(status) {
    const prevStatus = this.status;
    this.status = status;
    this.log(`status changed from [${prevStatus}] to [${status}]`);
    this.onStatusChanged?.(status);
  }

  log = (...args) => {
    this.logger([`[${this.prefix}]`, ...args], 'log');
  };

  error = (...args) => {
    this.logger([`[${this.prefix}]`, ...args], 'error');
  };

  warn = (...args) => {
    this.logger([`[${this.prefix}]`, ...args], 'warn');
  };

  onSubscribe = (config) => this.sendSubscribeMessage(config, true);
}
